// Sammelansicht aller Monatsberichte (nur Admin).
//
// Nach dem Monatslauf am 1. liegen die Entwürfe aller Kunden hier — ein Ort zum
// Durchsehen, Freigeben und Verschicken, statt sich durch jeden Kunden einzeln
// zu klicken. Nach Monat gruppiert, weil das die Arbeitsreihenfolge ist.

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertTriangle } from 'lucide-react';
import { listAllReports } from '@/lib/analytics-api';
import BerichtKachel from '@/components/BerichtKachel';

function monatLabel(month: string): string {
  const d = new Date(`${month.slice(0, 7)}-01T00:00:00Z`);
  return d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export default function BerichteUebersicht() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['alle-berichte'] });

  const { data, isLoading } = useQuery({ queryKey: ['alle-berichte'], queryFn: listAllReports });

  if (isLoading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gray-300" /></div>;
  }

  const berichte = data?.berichte || [];
  if (!berichte.length) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white px-5 py-10 text-center">
        <p className="text-sm text-gray-500">Noch keine Berichte vorhanden.</p>
        <p className="mt-1 text-xs text-gray-400">
          Der Monatslauf erzeugt am 1. für jeden Kunden einen Entwurf. Einzeln erstellen kannst du sie
          unter Analytics → Empfehlungen → Monats-Report.
        </p>
      </div>
    );
  }

  // Nach Monat gruppieren — so arbeitet man sie auch ab
  const monate = Array.from(new Set(berichte.map((b) => b.month.slice(0, 7))));

  // Ampel: Optimierungen müssen WÄHREND des Monats laufen — am 1. ist es zu
  // spät fürs Kapitel „Das haben wir verbessert". Grün unter 30 Tagen, gelb
  // darüber, rot ab 60 oder wenn noch nie eine Runde lief.
  const stand = data?.optimierungs_stand || [];
  const tageSeit = (iso: string | null) =>
    iso == null ? null : Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

  return (
    <div className="space-y-5">
      {stand.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white px-5 py-3">
          <p className="text-xs font-medium uppercase tracking-wider text-gray-500">
            Letzte Empfehlungsrunde je Kunde
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {stand.map((s) => {
              const tage = tageSeit(s.letzte_runde);
              const farbe =
                tage == null || tage >= 60
                  ? 'bg-red-50 border-red-200 text-red-800'
                  : tage >= 30
                    ? 'bg-amber-50 border-amber-200 text-amber-800'
                    : 'bg-green-50 border-green-200 text-green-800';
              const punkt = tage == null || tage >= 60 ? 'bg-red-500' : tage >= 30 ? 'bg-amber-500' : 'bg-green-500';
              return (
                <span
                  key={s.customer_id}
                  className={`inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-xs font-medium ${farbe}`}
                  title="Wann lief zuletzt eine Empfehlungsrunde (Analytics → Empfehlungen)? Was im Monatsbericht als Verbesserung stehen soll, muss während des Monats umgesetzt sein."
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${punkt}`} />
                  {s.kunde}
                  <span className="font-normal opacity-75">
                    {tage == null ? 'noch nie' : tage === 0 ? 'heute' : `vor ${tage} Tag${tage === 1 ? '' : 'en'}`}
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      )}
      {monate.map((m) => {
        const zeilen = berichte.filter((b) => b.month.slice(0, 7) === m);
        const offen = zeilen.filter((z) => !z.released_at).length;
        return (
          <div key={m} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
            <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-5 py-3">
              <h2 className="text-sm font-semibold text-gray-900">{monatLabel(m)}</h2>
              <span className="text-xs text-gray-400">
                {zeilen.length} Bericht{zeilen.length > 1 ? 'e' : ''}
              </span>
              {offen > 0 && (
                <span className="ml-auto rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
                  {offen} noch nicht freigegeben
                </span>
              )}
            </div>

            {/* Kacheln statt Zeilen: Jede zeigt das echte A4-Blatt, Klick auf die
                Lupe zieht es gross auf. So sieht Nico vor dem Freigeben, was
                beim Kunden ankommt, ohne jede Datei zu oeffnen. */}
            {/* Gemeinsame Kachel mit allen vier Aktionen — identisch zum
                Analytics-Tab, damit beide nicht auseinanderlaufen. */}
            <div className="grid gap-4 px-5 py-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {zeilen.map((z) => (
                <BerichtKachel key={z.id} b={z} invalidate={invalidate} />
              ))}
            </div>
          </div>
        );
      })}

      <p className="flex items-start gap-2 text-xs text-gray-400">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          <strong className="font-medium text-gray-500">Senden</strong> macht den Bericht im Kundenkonto sichtbar; die
          E-Mail ist eine getrennte Bestätigung und geht nur raus, wenn beim Kunden eine Berichts-E-Mail hinterlegt ist.
          <strong className="font-medium text-gray-500"> Bearbeiten</strong> schreibt nur den Text um — die Zahlen und
          das PDF-Zahlenwerk bleiben unverändert. <strong className="font-medium text-gray-500">Neu generieren</strong>{' '}
          verwirft den Text und schreibt ihn aus den aktuellen Daten neu.
        </span>
      </p>
    </div>
  );
}
